import React from 'react';
import RadarChart from './RadarChart';
import BarComparisonChart from './BarComparisonChart';
import { BRAND_GREEN, BRAND_GOLD } from '../../theme/sucronTheme';


/**
 * StatCardPreview - Container visual para insights compartilháveis
 * 
 * @param {String} type - Tipo de visualização: 'radar' | 'bar' | 'table' | 'highlight'
 * @param {Object} data - Dados a serem renderizados (estrutura varia por tipo)
 * @param {String} title - Título do insight
 * @param {String} subtitle - Subtítulo/descrição
 * @param {Function} onDownload - Callback para download (recebe ref do card)
 * 
 * Exemplo 'radar':
 * data = {
 *   players: [{ player_name: 'Messi', goals: 95, ... }],
 *   dimensions: ['goals', 'assists', 'passing']
 * }
 * 
 * Exemplo 'bar':
 * data = {
 *   leagues: [{ league_name: 'Premier League', goals_per_game: 2.8 }],
 *   metric: 'goals_per_game',
 *   metricLabel: 'Gols por Jogo'
 * }
 * 
 * Exemplo 'table':
 * data = {
 *   headers: ['Jogador', 'Gols', 'Assistências'],
 *   rows: [['Messi', 45, 20], ['Haaland', 52, 8]]
 * }
 * 
 * Exemplo 'highlight':
 * data = {
 *   stat: '52',
 *   label: 'Gols',
 *   player: 'Haaland',
 *   context: '2024/25'
 * }
 */
export default function StatCardPreview({ type, data, title, subtitle, onDownload }) {
  const cardRef = React.useRef(null);

  const renderContent = () => {
    switch (type) {
      case 'radar':
        return (
          <RadarChart 
            players={data?.players || []}
            dimensions={data?.dimensions || []}
          />
        );

      case 'bar':
        return (
          <BarComparisonChart
            leagues={data?.leagues || []}
            metric={data?.metric}
            metricLabel={data?.metricLabel}
          />
        );

      case 'table':
        return (
          <div className="overflow-auto max-h-80">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: BRAND_GREEN }}>
                  {(data?.headers || []).map((header, idx) => (
                    <th 
                      key={idx}
                      className="px-4 py-3 text-left text-sm font-bold text-white"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((row, rowIdx) => (
                  <tr 
                    key={rowIdx}
                    className="border-b border-gray-200 hover:bg-gray-50 transition"
                  >
                    {row.map((cell, cellIdx) => (
                      <td 
                        key={cellIdx}
                        className="px-4 py-3 text-sm text-gray-700"
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'highlight':
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <div 
              className="text-8xl font-black mb-4"
              style={{ color: BRAND_GOLD }}
            >
              {data?.stat || '—'}
            </div>
            <div className="text-2xl font-bold text-gray-800 mb-2">
              {data?.label || 'Estatística'}
            </div>
            <div className="text-lg text-gray-600 mb-1">
              {data?.player || 'Jogador'}
            </div>
            <div className="text-sm text-gray-500">
              {data?.context || 'Temporada'}
            </div>
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-64 text-gray-400">
            <p>Tipo de visualização não suportado</p>
          </div>
        );
    }
  };

  React.useEffect(() => {
    if (onDownload) {
      onDownload(cardRef);
    }
  }, [onDownload]);

  return (
    <div 
      ref={cardRef}
      className="bg-white rounded-xl shadow-lg overflow-hidden"
      style={{ maxWidth: '800px', width: '100%' }}
    >
      {/* Header com Gradiente */}
      <div 
        className="px-6 py-4"
        style={{
          background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #374151 100%)`
        }}
      >
        <h2 className="text-2xl font-bold text-white mb-1">
          {title || 'Insight Editorial'}
        </h2>
        {subtitle && (
          <p className="text-sm text-green-100">
            {subtitle}
          </p>
        )}
      </div>

      {/* Conteúdo */}
      <div className="p-6">
        {renderContent()}
      </div>

      {/* Footer com Branding */}
      <div 
        className="px-6 py-3 border-t-2 flex items-center justify-between"
        style={{ borderColor: BRAND_GOLD }}
      >
        <div className="flex items-center gap-2">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            AS
          </div>
          <span className="text-sm font-semibold text-gray-700">
            ArenaStats Editorial
          </span>
        </div>
        <div className="text-xs text-gray-500">
          {new Date().toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: 'short', 
            year: 'numeric' 
          })}
        </div>
      </div>
    </div>
  );
}
